<template>
  <div class="component">
    <div class="titleIndex">
      <span>
        <b>Documentos</b>
      </span>
    </div>
    <hr />
    <div class="container-fluid">
      <div class="row">
        <div class="col-lg-3 col-12">
          <multiselect
            v-model="selectedFilter"
            :options="filterOptions"
            :searchable="true"
            :internal-search="false"
            :close-on-select="true"
            :show-labels="false"
            placeholder="Filtre por Nome, CNPJ ou Empresa"
            track-by="value"
            label="text"
            class="flex-grow-1"
            @search-change="onSearchChange"
          >
            <template slot="noOptions">
              Nenhuma unidade encontrada
            </template>
          </multiselect>
        </div>
        <div class="col-lg-9 col-12">
          <div class="d-flex h-100 numDocs align-items-center">
            <label for="documentsPerPage" class="mr-2 mb-0">Itens por página</label>
            <select
              id="documentsPerPage"
              v-model.number="perPage"
              class="form-control"
              style="width: auto"
              @change="onPerPageChange(perPage)"
            >
              <option v-for="option in perPageOptions" :key="option" :value="option">{{ option }}</option>
            </select>
          </div>
        </div>
      </div>
      <div class="row" style="margin-top: 4vh">
        <div class="col-12">
          <b-table :fields="fields" :items="paginatedCompanies">
            <template v-slot:cell(id)="data">
                <span
                    class="text-primary"
                    style="cursor:pointer"
                    @click="handleClick(data.item)"
                >
                <font-awesome-icon icon="external-link-alt"  />
              </span>
            </template>
            <template v-slot:cell(updated_at)="data">{{formatDate(data.value)}}</template>
          </b-table>
          <small>{{rows}} Unidades localizadas</small>
          <b-pagination
            class="float-right"
            v-model="currentPage"
            :total-rows="rows"
            :per-page="perPage"
            aria-controls="my-table"
          ></b-pagination>
        </div>
      </div>
    </div>
  </div>
</template>
<script>
import axios from "@/api/axios";
import moment from "moment";
import Multiselect from "vue-multiselect";

const PER_PAGE_OPTIONS = [10, 20, 50, 100];
const PER_PAGE_STORAGE_KEY = 'documents_index_per_page';

function loadStoredPerPage() {
  const stored = Number(localStorage.getItem(PER_PAGE_STORAGE_KEY));
  return PER_PAGE_OPTIONS.includes(stored) ? stored : PER_PAGE_OPTIONS[0];
}

export default {
  name: "IndexDocuments",
  components: {
    Multiselect
  },
  computed: {
    paginatedCompanies() {
      const start = (this.currentPage - 1) * this.perPage;
      return this.filteredCompanies.slice(start, start + this.perPage);
    }
  },
  data() {
    return {
      perPage: loadStoredPerPage(),
      perPageOptions: PER_PAGE_OPTIONS,
      currentPage: 1,
      rows: 4,
      search: "",
      selectedFilter: { text: 'Todas as Unidades', value: 'all' },
      filterOptions: [{ text: 'Todas as Unidades', value: 'all' }],
      allCompanies: [], // base inteira, carregada uma vez (endpoint cacheado no backend)
      filteredCompanies: [],
      fields: [
        { key: "id", label: "Visualizar" },
        { key: "corporate_group.name", label: "Empresa" },
        { key: "corporate_name", label: "Unidades", sortable: true },
        { key: "updated_at", label: "Modificado em", sortable: true }
      ],
      role: localStorage.getItem('auth.role'),
      email: localStorage.getItem('auth.email')
    };
  },
  async mounted() {
    this.showCompanyWarningIfAny();
    await this.loadAllCompanies();
  },
  methods: {
    showCompanyWarningIfAny() {
      const warning = this.$route.query.company_warning;
      if (!warning) {
        return;
      }

      const messages = {
        'no-company': 'Selecione uma empresa para continuar.',
        'not-found': 'Empresa não encontrada.',
        'access-denied': 'Você não tem acesso a essa empresa.',
      };

      this.$bvToast.toast(messages[warning] || messages['access-denied'], {
        title: 'Aviso',
        toaster: 'b-toaster-top-center',
        solid: true,
        variant: 'warning',
      });

      // Limpa o parâmetro pra não repetir o aviso se a página for recarregada
      this.$router.replace({ query: {} });
    },

    // Único lugar que decide se uma empresa bate com o termo buscado — usado tanto
    // pra filtrar a tabela quanto pra montar as sugestões do combo, pra não correr
    // o risco de as duas coisas responderem campos diferentes (mesmo padrão de
    // ListCompanies.vue, ver #360).
    matchesSearch(company, term) {
      const query = term.toLowerCase();
      const name = company.corporate_name?.toLowerCase() ?? '';
      const cnpj = company.cnpj?.toLowerCase() ?? '';
      const group = company.corporate_group?.name?.toLowerCase() ?? '';
      return name.includes(query) || cnpj.includes(query) || group.includes(query);
    },

    // Carrega a base inteira de uma vez só (endpoint cacheado no backend, ver #360),
    // pra o combo já nascer com sugestões de todas as unidades, não só da página atual.
    async loadAllCompanies() {
      const loader = this.$loading.show();
      try {
        const response = await axios.get(
          `${process.env.VUE_APP_API_HOST}/api/admin/companies`,
          {
            params: {
              not_paginated: true,
              include: 'corporate_group,address',
              user_email: this.email,
              user_role: this.role
            }
          }
        );

        this.allCompanies = (response.data.data || [])
          .sort((a, b) => new Date(b.updated_at) - new Date(a.updated_at));
        this.applyFilter();
      } catch (error) {
        console.error('Erro ao carregar unidades:', error);
      } finally {
        loader.hide();
      }
    },

    applyFilter() {
      this.filteredCompanies = this.search
        ? this.allCompanies.filter(company => this.matchesSearch(company, this.search))
        : this.allCompanies;
      this.rows = this.filteredCompanies.length;

      this.filterOptions = [
        { text: 'Todas as Unidades', value: 'all' },
        ...this.filteredCompanies
          .filter(c => c.corporate_name)
          .map(c => ({
            text: `${c.corporate_name}${c.corporate_group?.name ? ' - ' + c.corporate_group.name : ''}`,
            value: c.id
          }))
      ];
    },

    // Chamado a cada tecla digitada no combo. Como a base inteira já está carregada,
    // o filtro é instantâneo, sem ida ao servidor.
    onSearchChange(term) {
      if (!term) {
        // O vue-multiselect dispara search-change com string vazia ao perder o foco
        // (limpeza interna do próprio campo) — não é o usuário pedindo pra ver todas
        // de novo. Ignora; pra isso existe a opção "Todas as Unidades" no combo.
        return;
      }

      this.search = term;
      this.currentPage = 1;
      this.applyFilter();
    },

    onPerPageChange(value) {
      localStorage.setItem(PER_PAGE_STORAGE_KEY, value);
      this.currentPage = 1;
    },

    formatDate: function(date) {
      return moment(date).format("DD/MM/YYYY HH:mm");
    },
    handleClick(item) {
      localStorage.setItem('companyName', item.corporate_name);
      localStorage.setItem('extras', item.extra_fields_apr);
      localStorage.setItem('company_id', item.id);
      localStorage.setItem('corporate_group_id', item.corporate_group_id);
      this.$router.push({ name: 'Company_Documents', params: { company_id: item.id } });
    },
  },
  watch: {
    selectedFilter: {
      handler(newValue) {
        if (!newValue || newValue.value === 'all') {
          // Voltou pra "Todas as Unidades": limpa a busca e mostra tudo de novo.
          this.search = "";
          this.currentPage = 1;
          this.applyFilter();
          return;
        }

        // Escolheu uma sugestão específica: mostra só essa empresa.
        const picked = this.allCompanies.find(company => company.id === newValue.value);
        if (picked) {
          this.filteredCompanies = [picked];
          this.rows = 1;
          this.currentPage = 1;
        }
      },
      deep: true
    }
  }
};
</script>
<style src="vue-multiselect/dist/vue-multiselect.min.css"></style>
<style lang="scss">
@import "node_modules/bootstrap/scss/bootstrap";
@import "node_modules/bootstrap-vue/src/index.scss";
@import "src/styles/General.scss";

.titleIndex {
  padding: 2vh 2vw 0 1vw;
}
.numDocs {
  justify-content: flex-end;
  align-items: center;
}
</style>
